import { BKJSSFBarSearchInput } from 'components/BKJSSFBarSearchInput';
import { Portal } from 'components/Portal/Portal';
import { QuerySwitch } from 'components/QuerySwitch';
import { useClickOutside } from 'hooks';
import { FC, Fragment, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'hooks/useTranslation';
import { usePopper } from 'react-popper';
import { RequestTeamMemberForOrderQuery } from 'types/graphql';
import { mergeRefs } from 'utils';
import { getJ360ElementNode } from 'utils/getJ360ElementNode';
import { RequestTeamMemberForOrderContext } from 'components/RequestTeamMemberForOrder/RequestTeamMemberForOrder.context';
import { IProps } from 'components/RequestTeamMemberForOrder/index';
import { requestForOrdersPopperModifiers } from 'components/RequestTeamMemberForOrder/RequestTeamMemberForOrder.constants';
import { TeamMembersList } from 'components/RequestTeamMemberForOrder/src/TeamMemberList';
import { DropdownWrapper } from 'components/RequestTeamMemberForOrder/RequestTeamMemberForOrder.styled';

export const RequestTeamMemberForOrderComponent: FC<IProps> = ({
  name,
  onChange,
  value,
  ...rest
}) => {
  const { t } = useTranslation();
  const {
    values: queryContextValues,
    onChange: queryContextOnChange,
    loadMore,
  } = useContext(RequestTeamMemberForOrderContext);

  const dropdownRef = useRef<HTMLElement | null>(null);

  const [referenceElement, setReferenceElement] = useState<Element | null>(null);
  const [popperElement, setPopperElement] = useState(null);
  const [targetElement, setTargetElement] = useState<HTMLElement | null>(
    getJ360ElementNode('dropdown-root'),
  );
  const [isOpen, setOpen] = useState(false);
  const { styles, attributes } = usePopper(referenceElement, popperElement, {
    modifiers: requestForOrdersPopperModifiers,
  });

  useEffect(() => {
    if (!targetElement) {
      const element = document.createElement('div');
      element.setAttribute('id', 'dropdown-root');
      document.body.appendChild(element);
      setTargetElement(element);
    }
  }, []); // eslint-disable-line

  const handleDropdownOpen = useCallback(() => {
    if (!isOpen) setOpen(true);
  }, [isOpen]);

  const handleDropdownClose = useCallback(() => {
    if (isOpen) setOpen(false);
  }, [isOpen]);

  const handleChange = useCallback(
    (event) => {
      queryContextOnChange(event);
      handleDropdownOpen();
    },
    [queryContextOnChange, handleDropdownOpen],
  );

  const handleSelect = useCallback(
    (event) => {
      const { value: nextValue } = event.target;
      const val = value === nextValue ? '' : event.target.value;

      onChange({ ...event, target: { ...event.target, name, value: val } });
      queryContextOnChange({ ...event, target: { ...event.target, name, value: val } });

      handleDropdownClose();
    },
    [handleDropdownClose, name, onChange, queryContextOnChange, value],
  );

  const handleClearSearch = useCallback(() => {
    onChange({ target: { value: '', name } });
    queryContextOnChange({ target: { value: '', name } });
  }, [queryContextOnChange, name, onChange]);

  useClickOutside(
    [dropdownRef && dropdownRef.current, targetElement && targetElement],
    handleDropdownClose,
  );

  const TeamMembersListWithProps = useCallback(
    ({ data }: { data: RequestTeamMemberForOrderQuery }) => (
      <TeamMembersList
        data={data}
        onLoadMore={loadMore}
        name={name}
        value={value}
        onSelect={handleSelect}
      />
    ),
    [handleSelect, loadMore, name, value],
  );

  const teamMemberQueryFilterValue = queryContextValues[name];

  return (
    <Fragment>
      <BKJSSFBarSearchInput
        {...rest}
        onClick={handleDropdownOpen}
        ref={setReferenceElement}
        placeholder={t('common:SEARCH_MEMBER')}
        value={value || ''}
        teamMemberQueryFilterValue={teamMemberQueryFilterValue}
        onChange={handleChange}
        name={name}
        onClearSearch={handleClearSearch}
      />
      {!!isOpen && !!targetElement && (
        <Portal targetElement={targetElement}>
          <DropdownWrapper
            ref={mergeRefs([dropdownRef, setPopperElement])}
            style={{ ...styles.popper }}
            {...attributes.popper}
          >
            <QuerySwitch
              loadingIconSize="XSmall"
              context={RequestTeamMemberForOrderContext}
              component={TeamMembersListWithProps}
            />
          </DropdownWrapper>
        </Portal>
      )}
    </Fragment>
  );
};
